import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { Gem, Car, Wrench, Info, Loader2 } from 'lucide-react';
import { api, compact } from '../lib/api';
import { useAuth } from '../lib/store';
import { themeOf, ARENA, CAR_SLOT_THEME } from '../lib/classTheme';
import { ArenaFloor, ArenaTabs, ArenaEmpty, Laurel } from '../components/ArenaFrame';
import { Sheet, SkeletonList } from '../components/ui';
import { ShopCard, BuyPreview, RarityLegend } from './PetShop';

export default function PartsShop() {
  const [sp, setSp] = useSearchParams();
  const qc = useQueryClient();
  const { user, load } = useAuth();
  const [slot, setSlot] = useState(sp.get('slot') || 'engine');
  const [preview, setPreview] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['partsShop'], queryFn: () => api.get('/cars/shop').then(r => r.data),
  });
  const { data: car } = useQuery({ queryKey: ['car'], queryFn: () => api.get('/cars').then(r => r.data) });

  const buy = useMutation({
    mutationFn: ({ partId, rarity }) => api.post('/cars/buy-part', { partId, rarity }),
    onSuccess: ({ data: d }) => {
      toast.success(`⚙️ ${d.name} acquired! −💎${d.price}`);
      setPreview(null);
      qc.invalidateQueries(['partsShop', 'car', 'carParts']);
      load();
    },
    onError: e => toast.error(e.message),
  });

  useEffect(() => { setSp(slot ? { slot } : {}, { replace: true }); }, [slot]);

  if (isLoading || !data) return <SkeletonList n={4} h="h-40" />;

  const t = themeOf(data.classKey);
  const items = (data.catalog?.[slot] || []).map(p => ({ ...p, itemId: p.partId }));
  const balance = data.balance ?? user?.points_balance ?? 0;

  if (!car?.car) {
    return (
      <ArenaEmpty emoji="🏎️" title="No chariot yet"
        sub="Claim a chariot first to browse tuning parts."
        action={<Link to="/garage" className="btn-primary btn-sm">Go to garage</Link>} />
    );
  }

  return (
    <div className="space-y-4 animate-fade-up">
      <div className="card-arena arena-arches overflow-hidden">
        <ArenaFloor classKey={data.classKey} className="p-5" showCrowd={false}>
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-2xl grid place-items-center shrink-0"
              style={{
                background: `${t.hex}28`, border: `1.5px solid ${t.hex}`,
                boxShadow: `0 0 20px -4px ${t.hex}`,
              }}>
              <Wrench size={22} style={{ color: ARENA.goldL }} />
            </div>
            <div className="flex-1 min-w-0 text-white">
              <div className="flex items-center gap-1.5 mb-0.5">
                <Laurel size={18} color={ARENA.goldL} />
                <span className="text-[9px] font-display font-black uppercase tracking-[0.2em]"
                  style={{ color: ARENA.goldL }}>Fabrica</span>
              </div>
              <h1 className="font-display text-xl font-extrabold tracking-wide"
                style={{ color: ARENA.goldL, textShadow: '0 2px 14px rgba(201,151,58,.45)' }}>
                Tuning Parts
              </h1>
              <p className="text-[11px] font-semibold" style={{ color: 'rgba(232,220,200,.7)' }}>
                {car.car.name} · {car.car.modelType}
              </p>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between rounded-2xl px-4 py-2.5"
            style={{ background: 'rgba(232,200,116,.14)', border: '1px solid rgba(232,200,116,.28)' }}>
            <span className="text-[10px] font-display font-black uppercase tracking-wider"
              style={{ color: 'rgba(232,220,200,.7)' }}>Treasury</span>
            <span className="font-display text-xl font-extrabold flex items-center gap-1.5"
              style={{ color: ARENA.goldL }}>
              <Gem size={16} /> {compact(balance)}
            </span>
          </div>
        </ArenaFloor>

        <div className="p-3" style={{ background: 'var(--sand-100)' }}>
          <Link to="/garage" className="btn-ghost btn-sm w-full">
            <Car size={14} /> Back to garage
          </Link>
        </div>
      </div>

      {balance < 500 && (
        <div className="card p-4" style={{ borderColor: ARENA.gold, borderWidth: 2 }}>
          <p className="font-display font-bold text-sm flex items-center gap-1.5"
            style={{ color: ARENA.goldD }}>
            <Info size={14} /> Need more points?
          </p>
          <ul className="text-xs font-semibold mt-2 space-y-1" style={{ color: 'var(--stone-600)' }}>
            <li>🛒 Shop at local merchants — <b>1 point per unit</b></li>
            <li>🏁 Win races — <b>45 points</b></li>
            <li>🧠 Play trivia — up to <b>90 points</b></li>
          </ul>
          <div className="grid grid-cols-2 gap-2 mt-3">
            <Link to="/shops" className="btn-primary btn-sm">Find shops</Link>
            <Link to="/race" className="btn-ghost btn-sm">Race</Link>
          </div>
        </div>
      )}

      <ArenaTabs value={slot} onChange={setSlot}
        tabs={Object.entries(CAR_SLOT_THEME).map(([k, s]) => ({
          key: k, label: s.label, icon: s.icon,
        }))} />

      <div className="flex items-center gap-2 px-1">
        <span className="text-xl">{CAR_SLOT_THEME[slot].icon}</span>
        <div>
          <p className="font-display font-bold text-sm">{CAR_SLOT_THEME[slot].label}</p>
          <p className="text-[11px] font-semibold" style={{ color: 'var(--stone-400)' }}>
            {CAR_SLOT_THEME[slot].hint}
          </p>
        </div>
      </div>

      {!items.length ? (
        <ArenaEmpty emoji="⚙️" title="Not manufactured yet"
          sub="These parts are still in production. Check back soon!" />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {items.map((it, i) => (
            <motion.div key={`${it.partId}-${it.rarity}`}
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}>
              <ShopCard item={it} onClick={() => setPreview(it)} />
            </motion.div>
          ))}
        </div>
      )}

      <RarityLegend items={items} />

      <Sheet open={!!preview} onClose={() => setPreview(null)} title={preview?.name || ''}>
        {preview && <BuyPreview item={preview} balance={balance}
          busy={buy.isPending} slotMeta={CAR_SLOT_THEME[preview.slot]}
          onBuy={() => buy.mutate({ partId: preview.partId, rarity: preview.rarity })} />}
      </Sheet>
    </div>
  );
}